import gsap from 'gsap';

/**
 * Initializes the 9:16 swipeable reel card deck.
 * @param {Function} openModal — callback to open the case-study modal with card data
 */
export async function initDeck(openModal) {
  const reelRes = await fetch('/reel_videos.json');
  const REEL_VIDEOS = await reelRes.json();

  const DECK_SCENE = document.getElementById('js-deck-scene');
  let deckCards = [];
  let topIndex = 0;
  let isDragging = false;
  let deckStartX = 0;
  let deckStartY = 0;
  let topCardDOM = null;

  // Dynamically build cards based on current dataset filter
  function renderDeck(videoArray) {
    DECK_SCENE.innerHTML = '';
    deckCards = [];

    // Only load the exact available videos, do not infinitely duplicate them
    for (let i = 0; i < videoArray.length; i++) {
      const data = { ...videoArray[i], format: 'vertical' };
      const card = document.createElement('div');
      card.className = 'reel-card';
      card.innerHTML = `<div class="video-spinner"></div><video loop muted playsinline preload="auto" controlsList="nodownload"><source src="${data.src}"></video>`;

      const vid = card.querySelector('video');
      const spinner = card.querySelector('.video-spinner');
      if (vid) {
        vid.addEventListener('canplay', () => spinner.classList.add('hidden'), { once: true });

        // UX Micro-interaction: Nudge the card when the video is about to loop
        vid.addEventListener('timeupdate', () => {
          if (card.classList.contains('active-reel') && !isDragging && !card.hasTilted && vid.duration > 0 && vid.currentTime >= vid.duration - 0.4) {
            card.hasTilted = true; // Prevents infinite nudging per appearance
            gsap.to(card, {
              rotationZ: (Math.random() > 0.5 ? 6 : -6),
              x: (Math.random() > 0.5 ? 45 : -45),
              duration: 0.6,
              ease: 'power2.out'
            });
          }
        });
      }
      card.vidData = data;
      DECK_SCENE.appendChild(card);
      deckCards.push(card);
    }
    topIndex = deckCards.length - 1;
  }

  // Initial layout
  renderDeck(REEL_VIDEOS);

  function updateDeckVisuals(immediate = false) {
    deckCards.forEach((card, index) => {
      const depth = topIndex - index;
      if (depth < 0) return;

      const targetScale = 1 - depth * 0.05;
      const targetY = depth * 30;
      const targetOpacity = 1 - depth * 0.2;

      gsap.to(card, {
        y: targetY,
        scale: targetScale,
        opacity: targetOpacity,
        rotationZ: 0,
        x: 0,
        duration: immediate ? 0 : 0.6,
        ease: 'back.out(1.2)',
        onStart: () => {
          card.style.zIndex = index;
        },
      });

      const vid = card.querySelector('video');
      if (depth === 0) {
        card.classList.add('active-reel');
        if (vid) vid.play().catch(() => { });
      } else {
        card.classList.remove('active-reel');
        card.hasTilted = false; // Reset UX nudge when moved to back
        if (vid) {
          vid.pause();
          vid.currentTime = 0;
        }
      }
    });
  }

  // Initial layout
  updateDeckVisuals(true);

  // Pointer events
  DECK_SCENE.addEventListener('pointerdown', (e) => {
    if (e.target.closest('.glass-modal')) return;
    topCardDOM = deckCards[topIndex];
    if (!topCardDOM || e.target.closest('.reel-card') !== topCardDOM) return;

    isDragging = true;
    deckStartX = e.clientX;
    deckStartY = e.clientY;

    // Extrapolate exact current transforms so the drag doesn't snap abruptly
    topCardDOM._startX = gsap.getProperty(topCardDOM, "x") || 0;
    topCardDOM._startR = gsap.getProperty(topCardDOM, "rotationZ") || 0;

    gsap.killTweensOf(topCardDOM);
  });

  window.addEventListener('pointermove', (e) => {
    if (!isDragging || !topCardDOM) return;
    const dx = e.clientX - deckStartX;
    const dy = e.clientY - deckStartY;

    const rotate = topCardDOM._startR + (dx * 0.05);
    const finalX = topCardDOM._startX + dx;

    gsap.set(topCardDOM, { x: finalX, y: dy, rotationZ: rotate });
  });

  window.addEventListener('pointerup', (e) => {
    if (!isDragging || !topCardDOM) return;
    isDragging = false;

    const dx = e.clientX - deckStartX;
    const finalX = topCardDOM._startX + dx;

    // Click detection
    if (Math.abs(dx) < 5 && Math.abs(e.clientY - deckStartY) < 5) {
      if (topCardDOM.vidData) {
        openModal(topCardDOM.vidData);
      }
      gsap.set(topCardDOM, { x: 0, y: 0, rotationZ: 0 });
      return;
    }

    // Toss animation
    const swipeThreshold = window.innerWidth <= 900 ? 50 : 120;

    // Fallback: If only 1 card exists, rubber-band it instead of tossing it to prevent glitchy loops
    if (Math.abs(dx) > swipeThreshold && deckCards.length > 1) {
      const throwX = dx > 0 ? window.innerWidth : -window.innerWidth;
      gsap.to(topCardDOM, {
        x: throwX,
        y: e.clientY - deckStartY + 150,
        rotationZ: dx * 0.2,
        opacity: 0,
        duration: 0.6,
        ease: 'power2.out',
        onComplete: () => {
          const tossed = deckCards.pop();
          deckCards.unshift(tossed);
          deckCards.forEach((c) => DECK_SCENE.appendChild(c));

          gsap.set(tossed, { clearProps: 'all' });
          updateDeckVisuals();
        },
      });
    } else {
      updateDeckVisuals();
    }
  });

  // Dynamic Filter Generation
  const filterContainer = document.getElementById('reels-filters');
  const uniqueCategories = new Set();
  REEL_VIDEOS.forEach(v => {
    if (Array.isArray(v.category)) {
      v.category.forEach(c => uniqueCategories.add(c));
    } else if (v.category) {
      uniqueCategories.add(v.category);
    }
  });

  if (filterContainer && uniqueCategories.size > 0) {
    let html = '<button class="filter-btn active" data-category="All">All</button>';
    Array.from(uniqueCategories).sort().forEach(cat => {
      html += `<button class="filter-btn" data-category="${cat}">${cat}</button>`;
    });
    filterContainer.innerHTML = html;
  }

  // Filter interaction
  const filterBtns = document.querySelectorAll('#reels-filters .filter-btn');
  filterBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      if (btn.classList.contains('active')) return;

      filterBtns.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');

      const category = btn.dataset.category;
      let filteredData = category === 'All'
        ? REEL_VIDEOS
        : REEL_VIDEOS.filter(v => {
          if (Array.isArray(v.category)) return v.category.includes(category);
          return v.category === category;
        });

      if (!filteredData || filteredData.length === 0) {
        filteredData = REEL_VIDEOS;
      }

      isDragging = false;
      if (topCardDOM) gsap.killTweensOf(topCardDOM);

      gsap.to(DECK_SCENE, {
        opacity: 0,
        scale: 0.95,
        duration: 0.3,
        ease: 'power2.inOut',
        onComplete: () => {
          renderDeck(filteredData);
          updateDeckVisuals(true);

          gsap.to(DECK_SCENE, {
            opacity: 1,
            scale: 1,
            duration: 0.5,
            ease: 'back.out(1.2)'
          });
        }
      });
    });
  });

  return { deckContainer: document.querySelector('.deck-container') };
}
